import { openDB } from 'idb'

import { type KeyValueStore } from './KeyValueStore'

export class IndexedDBKeyValueStore<T> implements KeyValueStore<T> {
  private storeName
  private db

  constructor(storeName: string) {
    storeName = storeName.trim()

    if (storeName === '') {
      throw new TypeError('"storeName" should not be empty')
    }

    this.storeName = storeName
    this.db = openDB(`KV_${storeName}`, 1, {
      upgrade(db) {
        db.createObjectStore(storeName)
      },
    })
  }

  async get(key: string): Promise<T | undefined> {
    return (await this.db).get(this.storeName, key)
  }

  async set(key: string, value: T): Promise<void> {
    await (await this.db).put(this.storeName, value, key)
  }

  async remove(key: string): Promise<void> {
    return (await this.db).delete(this.storeName, key)
  }

  async keys(): Promise<Array<string>> {
    return (await this.db).getAllKeys(this.storeName) as Promise<Array<string>>
  }

  async values(): Promise<Array<T>> {
    return (await this.db).getAll(this.storeName)
  }

  async entries(): Promise<Array<[string, T]>> {
    // TODO: get the count, keys and values in a single transaction
    const count = await this.count()
    const keys = await this.keys()
    const values = await this.values()

    const entries: Array<[string, T]> = Array.from({ length: count }, (_, index) => [
      keys[index],
      values[index],
    ])

    return entries
  }

  async clear(): Promise<void> {
    return (await this.db).clear(this.storeName)
  }

  async count(): Promise<number> {
    return (await this.db).count(this.storeName)
  }
}
